/**
 * Wizard 2 Flow Router
 *
 * Steps: name...calculation → plan/overview → profile
 * Redirect to quiz/allergies when all 3 allergies selected
 */

class FlowRouter {
  constructor(flowConfig) {
    this.config = flowConfig;
    this.urlPrefix = flowConfig.urlPrefix;
    this.routes = this.buildRoutes();
    this.currentRoute = null;
    this.listeners = [];
    this._persistedQueryString = this._buildPersistedQueryString();
  }

  /**
   * Preserve marketing/referral query params across SPA routing.
   * This ensures third-party scripts (e.g. Social Snowball) can read params on initial load.
   */
  _buildPersistedQueryString() {
    try {
      const params = new URLSearchParams(window.location.search);
      // Internal param used for routing hydration; remove after applying.
      params.delete('__path');
      // Portal edit-profile entry flag — only for the initial profile load.
      params.delete('from');
      const s = params.toString();
      return s ? `?${s}` : '';
    } catch (e) {
      return '';
    }
  }

  _withPersistedQuery(path) {
    if (!path) return path;
    // If caller already provided a querystring, keep it (but still append persisted non-empty params if missing).
    if (path.includes('?')) return path;
    return `${path}${this._persistedQueryString || ''}`;
  }

  buildRoutes() {
    return {
      [`${this.urlPrefix}/quiz/get-started`]: 'get_started',
      [`${this.urlPrefix}/quiz/name`]: 'name',
      [`${this.urlPrefix}/quiz/spayed`]: 'spayed',
      [`${this.urlPrefix}/quiz/age`]: 'age',
      [`${this.urlPrefix}/quiz/breed`]: 'breed',
      [`${this.urlPrefix}/quiz/weight`]: 'weight',
      [`${this.urlPrefix}/quiz/composition`]: 'composition',
      [`${this.urlPrefix}/quiz/activity`]: 'activity',
      [`${this.urlPrefix}/quiz/allergies`]: 'allergies',
      [`${this.urlPrefix}/quiz/snacks`]: 'snacks',
      [`${this.urlPrefix}/quiz/email`]: 'email',
      [`${this.urlPrefix}/quiz/calculation`]: 'calculation',
      [`${this.urlPrefix}/plan/overview`]: 'plan_overview',
      [`${this.urlPrefix}/profile`]: 'profile'
    };
  }

  init() {
    window.addEventListener('popstate', () => this.handleRouteChange());
    window.addEventListener('hashchange', () => this.handleRouteChange());
    this.handleRouteChange();
  }

  getLocalePrefix() {
    const pathname = window.location.pathname;
    const match = pathname.match(/^(\/[a-z]{2}(-[a-z]{2})?)(?=\/|$)/);
    return match ? match[1] : '';
  }

  getCurrentPath() {
    const urlParams = new URLSearchParams(window.location.search);
    const pathParam = urlParams.get('__path');
    if (pathParam) {
      const localePrefix = this.getLocalePrefix();
      const newUrl =
        window.location.origin +
        localePrefix +
        pathParam +
        this._persistedQueryString +
        window.location.hash;
      window.history.replaceState({}, '', newUrl);
      return pathParam;
    }

    let path = window.location.pathname;
    const localePrefix = this.getLocalePrefix();
    if (localePrefix) path = path.slice(localePrefix.length) || '/';

    if (path.startsWith('/pages/wizard-two')) path = path.replace('/pages/wizard-two', '/pages/wizard-2');
    if (path.startsWith('/pages' + this.urlPrefix)) {
      path = path.replace('/pages' + this.urlPrefix, this.urlPrefix);
    } else if (path === `/pages${this.urlPrefix}`) {
      path = this.urlPrefix;
    }
    return path;
  }

  isRootUrl(path, step) {
    if (!step) return true;
    if (path === `${this.urlPrefix}/` || path === this.urlPrefix) return true;
    if (path === `/pages/wizard-2/` || path === `/pages/wizard-2`) return true;
    if (!path.includes('/quiz/') && !path.includes('/plan/') && path !== `${this.urlPrefix}/profile`) return true;
    return false;
  }

  isOnetimeHash() {
    return (window.location.hash || '').replace(/^#/, '') === 'onetime';
  }

  getStepFromPath(path) {
    if (path === `${this.urlPrefix}/plan/onetime`) return 'plan_onetime';
    if (path === `${this.urlPrefix}/plan/overview` && this.isOnetimeHash()) return 'plan_onetime';
    if (this.routes[path]) return this.routes[path];
    for (const [route, step] of Object.entries(this.routes)) {
      if (route.includes(':')) {
        const routePattern = route.replace(/:[^/]+/g, '([^/]+)');
        if (new RegExp(`^${routePattern}$`).test(path)) return step;
      }
    }
    return null;
  }

  getRouteParams(path) {
    const params = {};
    const urlParams = new URLSearchParams(window.location.search);
    const petParam = urlParams.get('pet');
    if (petParam) params.pet = decodeURIComponent(petParam);
    const photoParam = urlParams.get('photo');
    if (photoParam) params.photo = decodeURIComponent(photoParam);
    const fromParam = urlParams.get('from');
    if (fromParam) params.from = fromParam;
    return params;
  }

  navigate(step, params = {}, replace = false, queryParams = {}) {
    let path = null;
    let hash = '';
    if (step === 'plan_onetime') {
      path = `${this.urlPrefix}/plan/overview`;
      hash = '#onetime';
    } else {
      for (const [route, routeStep] of Object.entries(this.routes)) {
        if (routeStep === step) {
          path = route;
          if (step === 'profile' && params.pet) queryParams.pet = params.pet;
          break;
        }
      }
    }
    if (!path) return;

    if (Object.keys(queryParams).length > 0) {
      const qs = Object.keys(queryParams)
        .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(queryParams[k])}`)
        .join('&');
      path = `${path}?${qs}`;
    }

    const localePrefix = this.getLocalePrefix();
    if (localePrefix) path = localePrefix + path;
    path = this._withPersistedQuery(path) + hash;

    if (replace) window.history.replaceState({}, '', path);
    else window.history.pushState({}, '', path);
    this.handleRouteChange();
  }

  cameFromGetStarted() {
    try {
      return sessionStorage.getItem('wizard2_from_get_started') === '1';
    } catch (e) {
      return false;
    }
  }

  markCameFromGetStarted() {
    try {
      sessionStorage.setItem('wizard2_from_get_started', '1');
    } catch (e) {}
  }

  clearCameFromGetStarted() {
    try {
      sessionStorage.removeItem('wizard2_from_get_started');
    } catch (e) {}
  }

  navigateNext(currentStep) {
    if (currentStep === 'get_started') {
      this.navigate('name');
      return;
    }
    const stepOrder = [
      'name', 'spayed', 'age', 'breed', 'weight', 'composition',
      'activity', 'allergies', 'snacks', 'email', 'calculation',
      'plan_overview', 'profile'
    ];
    const idx = stepOrder.indexOf(currentStep);
    if (idx < stepOrder.length - 1) this.navigate(stepOrder[idx + 1]);
  }

  navigatePrevious(currentStep) {
    if (currentStep === 'get_started') return;
    if (currentStep === 'name' && this.cameFromGetStarted()) {
      this.navigate('get_started');
      return;
    }
    if (currentStep === 'plan_overview') {
      this.navigate('email');
      return;
    }
    if (currentStep === 'plan_onetime') {
      this.navigate('plan_overview');
      return;
    }
    const stepOrder = [
      'name', 'spayed', 'age', 'breed', 'weight', 'composition',
      'activity', 'allergies', 'snacks', 'email', 'calculation',
      'plan_overview', 'profile'
    ];
    const idx = stepOrder.indexOf(currentStep);
    if (idx > 0) this.navigate(stepOrder[idx - 1]);
  }

  handleRouteChange() {
    let path = this.getCurrentPath();
    if (path === `${this.urlPrefix}/plan/onetime`) {
      this.replaceUrlForStep('plan_onetime');
      path = this.getCurrentPath();
    }
    let step = this.getStepFromPath(path);
    let params = this.getRouteParams(path);
    const hash = (window.location.hash || '').replace(/^#/, '');

    if (this.currentRoute && this.currentRoute.step === step && this.currentRoute.path === path && this.currentRoute.hash === hash) {
      return;
    }

    const isRootUrl = !step ||
      path === `${this.urlPrefix}/` ||
      path === this.urlPrefix ||
      path === `/pages/wizard-2/` ||
      path === `/pages/wizard-2` ||
      (!path.includes('/quiz/') && !path.includes('/plan/') && path !== `${this.urlPrefix}/profile`);

    const flow = window.wizard2Flow;
    const state = flow?.state;

    if (step === 'get_started' && flow && state && typeof flow.hasQuizProgress === 'function' && flow.hasQuizProgress()) {
      const allowInSessionBack = flow.initialized && this.cameFromGetStarted();
      if (!allowInSessionBack) {
        const furthest = state.getFurthestValidStep() || 'name';
        const target = furthest === 'get_started' ? 'name' : furthest;
        this.clearCameFromGetStarted();
        this.replaceUrlForStep(target);
        path = this.getCurrentPath();
        step = target;
        state.setCurrentStep(target);
      }
    } else if (isRootUrl && flow && state && !flow.initialized) {
      const petData = state.getPetData();
      const hasLoadedData = petData.name || petData.gender || petData.ageGroup || petData.breed ||
        petData.weight !== null || state.customerId;
      if (hasLoadedData) {
        const furthest = state.getFurthestValidStep();
        if (furthest) {
          step = furthest;
          this.replaceUrlForStep(furthest);
          state.setCurrentStep(step);
        } else {
          step = 'name';
          state.setCurrentStep(step);
          this.replaceUrlForStep('name');
        }
      } else {
        step = 'name';
        state.setCurrentStep(step);
        this.replaceUrlForStep('name');
      }
    } else if (!step) {
      step = 'name';
      if (state) state.setCurrentStep(step);
    } else if (flow && state) {
      state.setCurrentStep(step);
    }

    this.currentRoute = { step, path, params, hash };
    this.notifyListeners(step, params);
  }

  getPathForStep(step) {
    if (step === 'plan_onetime') return `${this.urlPrefix}/plan/overview`;
    for (const [route, routeStep] of Object.entries(this.routes)) {
      if (routeStep === step) return route;
    }
    return null;
  }

  /** Canonical URL for a step (locale prefix + persisted marketing query params). */
  replaceUrlForStep(step) {
    const stepToPath = this.getPathForStep(step);
    if (!stepToPath) return;
    const localePrefix = this.getLocalePrefix();
    const hash = step === 'plan_onetime' ? '#onetime' : '';
    window.history.replaceState({}, '', this._withPersistedQuery((localePrefix || '') + stepToPath) + hash);
  }

  /**
   * Go to quiz/name and drop pet routing params (?pet=, ?photo=) from the URL.
   */
  navigateToNameFresh() {
    try {
      const params = new URLSearchParams(window.location.search);
      params.delete('pet');
      params.delete('photo');
      params.delete('__path');
      const s = params.toString();
      this._persistedQueryString = s ? `?${s}` : '';
    } catch (e) {
      this._persistedQueryString = '';
    }
    this.clearCameFromGetStarted();
    this.navigate('name', {}, true);
  }

  onRouteChange(callback) {
    this.listeners.push(callback);
  }

  offRouteChange(callback) {
    this.listeners = this.listeners.filter(l => l !== callback);
  }

  notifyListeners(step, params) {
    this.listeners.forEach(l => {
      try { l(step, params); } catch (e) { console.error('Route listener error:', e); }
    });
  }

  getCurrentRoute() {
    return this.currentRoute;
  }
}

window.FlowRouter = FlowRouter;
